import React, { useContext, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Stack,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { StoreCard } from "../../../lib/types/detailsTypes";
import { UserContext } from "../../../contexts/user-context";
import { ensureUrlHasProtocol } from "../../../lib/utils/fixUrlProtocol";

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface BackPopUpProps {
  card: StoreCard;
  onSettingsClick: () => void;
}

// ————— Section helper —————
type SectionProps = React.PropsWithChildren<{
  title: string;
}>;

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <Box mb={2}>
    {title && (
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

const BackPopUp: React.FC<BackPopUpProps> = ({ card, onSettingsClick }) => {
  /* pull everything we’ll reference from card in one place */
  const {
    logo,
    storeName,
    cardName,
    topLinkText,
    topLinkLink,
    cardDescription,
    stampCondition,
    claimRewardInstructions,
    rewards = [],
    disclaimer,
    websiteLink,
    socialURL,
    googleReviewLink,
    cardType,
    referralProgramActive,
    referralProgramDetails,
    cardLastScan,
  } = card;

  /* ---------------------------------------------------------------- */
  /*  Referral link helpers                                            */
  /* ---------------------------------------------------------------- */
  const { userData } = useContext(UserContext) || {};
  const referralCode = userData?.referralCode;
  const shortId = (card as any).shortId || (card as any).cardDocId || "";
  const referralLink = `https://www.stompcards.com/card/${card.shortID}?ref=${referralCode}`;

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleCopyReferral = () => {
    if (navigator?.clipboard && referralCode && card.shortID) {
      navigator.clipboard.writeText(referralLink).then(() => {
        setSnackbarOpen(true);
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f9f9f9",
        borderRadius: 2,
        boxShadow: "0 0 10px rgba(0 0 0 / 10%)",
        overflow: "hidden",
        maxWidth: 500,
        height: "70vh",
        p: 2,
      }}
    >
      {/* ──────────── SCROLLABLE CONTENT ──────────── */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {/* logo + store name */}
        {logo && (
          <Box textAlign="center" mb={1}>
            <img
              src={logo}
              alt={storeName}
              style={{
                width: 74,
                height: 74,
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
          </Box>
        )}
        <Typography variant="h5" align="center" fontWeight={700} gutterBottom>
          {storeName}
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          color="text.secondary"
          gutterBottom
        >
          {cardName ?? "Loyalty Card"}
        </Typography>
        <Divider sx={{ my: 2 }} />
        {/* optional top button */}
        {topLinkText && topLinkLink && (
          <Section title="">
            <Button
              variant="contained"
              fullWidth
              component="a"
              href={topLinkLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textDecoration: "none" }}
            >
              {topLinkText}
            </Button>
          </Section>
        )}

        {/* description / conditions */}
        {cardDescription && (
          <Section title="Description">
            <Typography variant="body2" whiteSpace="pre-line">
              {cardDescription}
            </Typography>
          </Section>
        )}
        {stampCondition && (
          <Section title="How to Earn">
            <Typography variant="body2" whiteSpace="pre-line">
              {stampCondition}
            </Typography>
          </Section>
        )}
        {claimRewardInstructions && (
          <Section title="How to Claim">
            <Typography variant="body2" whiteSpace="pre-line">
              {claimRewardInstructions}
            </Typography>
          </Section>
        )}
        {/* rewards list */}
        {rewards.length > 0 && (
          <Section title="Rewards">
            <List dense disablePadding sx={{ pl: 2, mt: -1 }}>
              {rewards.map(({ balanceRequired, reward }, idx) => (
                <ListItem key={idx} disableGutters sx={{ px: 0, py: 0.25 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                        {balanceRequired.toLocaleString()}{" "}
                        {cardType === "pointCard" ? "points" : "stamps"} –{" "}
                        {reward}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Section>
        )}

        {/* referral program */}
        {cardLastScan && referralProgramActive && referralCode && (
          <Section title="Refer & Earn">
            {referralProgramDetails && (
              <Typography variant="body2" whiteSpace="pre-line" sx={{ mb: 1 }}>
                {referralProgramDetails}
              </Typography>
            )}
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyReferral}
              sx={{ textTransform: "none" }}
            >
              Copy referral link
            </Button>
          </Section>
        )}

        {/* website + social + review links */}
        {(websiteLink || socialURL || googleReviewLink) && (
          <Section title="Links">
            <Stack direction="column" spacing={0.5}>
              {websiteLink && (
                <Button
                  variant="contained"
                  component="a"
                  href={ensureUrlHasProtocol(websiteLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Website
                </Button>
              )}
              {socialURL && (
                <Button
                  variant="contained"
                  component="a"
                  href={ensureUrlHasProtocol(socialURL)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Follow us!
                </Button>
              )}
              {googleReviewLink && (
                <Button
                  variant="contained"
                  component="a"
                  href={ensureUrlHasProtocol(googleReviewLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Leave us a review!
                </Button>
              )}
            </Stack>
          </Section>
        )}

        {/* terms / disclaimer */}
        {disclaimer && (
          <Section title="Terms of Use">
            <Typography
              variant="caption"
              component="ol"
              sx={{
                pl: 3,
                m: 0,
                "& li": {
                  marginBottom: "0.25rem",
                },
              }}
            >
              {disclaimer
                .split("\n")
                .filter((line) => line.trim() !== "")
                .map((line, index) => (
                  <li key={index}>{line}</li>
                ))}
            </Typography>
          </Section>
        )}

        {/* ──────────── SETTINGS BUTTON ──────────── */}
        <Button
          variant="contained"
          startIcon={<SettingsIcon />}
          fullWidth
          onClick={onSettingsClick}
          sx={{ borderRadius: 999, mb: 1 }}
        >
          Settings
        </Button>
      </Box>
      {/* Snackbar Feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Referral link copied!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BackPopUp;
